import { NgClass } from '@angular/common';
import { Component, OnInit, Input, input, SimpleChanges, ChangeDetectionStrategy, InputSignal, computed, effect } from '@angular/core';

import {
  ApexChart,
  ApexAxisChartSeries,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexStates,
  ApexGrid,
  ApexTitleSubtitle,
  NgApexchartsModule,
  ApexXAxis,
  ApexFill,
  ApexMarkers,
  ApexStroke,
} from 'ng-apexcharts';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export type CustomChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  grid: ApexGrid;
  subtitle: ApexTitleSubtitle;
  colors: string[];
  states: ApexStates;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  stroke: ApexStroke;
  fill: ApexFill;
  markers: ApexMarkers;
  labels: string[];
  tooltip: any;
};

declare global {
  interface Window {
    Apex: any;
  }
}

window.Apex = {
  chart: {
    toolbar: {
      show: false,
    },
  },
  tooltip: {
    shared: false,
  },
  legend: {
    show: false,
  },
};

@Component({
  selector: 'app-charts-widget1',
  templateUrl: './charts-widget1.component.html',
  standalone: true,
  imports: [NgApexchartsModule, NgClass, SpinnerComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartsWidget1Component implements OnInit {
  readonly cssClass = input<string>('');
  readonly chartCase = input<string>('');
  readonly data: InputSignal<({ title: string, amount: number, desc?: string } | { category: string; grossAmount: number })[]> = input.required();
  tooltipFormatter: InputSignal<((label: any, seriesIndex: number) => string) | undefined> = input();


  @Input() height: string = '';
  @Input({ required: true }) chartType: string = '';
  @Input() hoverLabels: string = '';
  @Input() isHorizontalBar: boolean = false;
  @Input() graphColors: string = '';
  @Input() isXLabels: boolean = false;
  @Input() isYLabels: boolean = false;
  @Input() isLegend: boolean = false;
  @Input() format: string = '';

  chartOptions: any = {};

  colors = this.getColors();
  seriesNameX: string = '';
  seriesNameY: string = '';
  designType: string = '';
  horizontal: boolean = false;
  claimHosData: any;

  private destroy$ = new Subject<void>();

  isLoading = computed(() => this.data()?.length === 0)

  constructor(
    private router: Router,
    private translate : TranslateService
  ) {
    effect(() => {
      if (this.data().length > 0) {
        if (this.chartType == "bar") {
          // )
        }
        this.rebuildChart()
      }
    })
  }


  private rebuildChart(): void {
    this.colors = this.getColors(this.graphColors);
    this.handleChartType(this.chartType);
  }

  get hasChartData(): boolean {
    const series = this.chartOptions?.series;
    if (!series) return false;

    if (Array.isArray(series) && series.length > 0 && typeof series[0] === 'number') {
      return series.some((n: any) => Number(n) > 0);
    }

    if (Array.isArray(series)) {
      return series.some((s: any) => {
        if (Array.isArray(s.data)) {
          return s.data.some((item: any) => this.isPositiveItem(item));
        }
        return this.arrayHasPositiveValue(series);
      });
    }

    return false;
  }

  private arrayHasPositiveValue(arr: any[]): boolean {
    if (!Array.isArray(arr) || arr.length === 0) return false;
    return arr.some(item => this.isPositiveItem(item));
  }

  private isPositiveItem(item: any): boolean {
    if (item == null) return false;

    if (typeof item === 'number') return item > 0;
    if (typeof item === 'string') return Number(item) > 0;

    if (typeof item === 'object') {
      const keysToCheck = ['y', 'value', 'amount', 'grossAmount', 'claimVolume', 'totalClaims'];
      for (const k of keysToCheck) {
        if (k in item && !isNaN(Number(item[k])) && Number(item[k]) > 0) return true;
      }

      for (const k of Object.keys(item)) {
        const num = Number((item as any)[k]);
        if (!isNaN(num) && num > 0) return true;
      }
    }

    return false;
  }



  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getColors(color: string = '') {
    if (color == '') {
      return [
        '#4B49AC',
        '#98BDFF',
        '#7DA0FA',
        '#7978E9',
        '#125773',
        '#27CDF2',
        '#488A99',
        '#C6D936',
      ];
    } else return [color];
  }

  handleChartType(chartType: string): void {
    switch (chartType) {
      case 'bar':
        this.chartOptions =
        {
          series: [
            {
              name: this.hoverLabels,
              data: this.data().map((item: any) => item.amount),
            },
          ],
          chart: {
            width: '100%',
            height: Number(this.height),
            type: 'bar',
          },
          plotOptions: {
            bar: {
              distributed: true,
              horizontal: this.isHorizontalBar,
              borderRadius: 5,
            },
          },
          dataLabels: {
            enabled: true,
            position: 'center',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
            formatter: (val: any) => {
              return val.toFixed(2) + this.format;
            },
          },
          stroke: {
            show: true,
            width: 2,
            colors: this.getColors(this.graphColors),
          },
          colors: this.getColors(this.graphColors),
          xaxis: {
            categories: this.data().map((item: any) => item.title),
            axisBorder: {
              show: false,
            },
            axisTicks: {
              show: false,
            },
            labels: {
              show: this.isXLabels,
            },
          },
          yaxis: {
            labels: {
              show: this.isYLabels,
            },
          },
          legend: {
            show: this.isLegend,
            position: 'bottom',
            floating: false,
            fontSize: '14px',
            labels: {
              colors: undefined,
              useSeriesColors: false,
            },
            markers: {
              width: 12,
              height: 12,
            },
            itemMargin: {
              horizontal: 5,
              vertical: 5,
            },
          },
          tooltip: {
            enabled: true,
            intersect: false,
            shared: true,
            followCursor: true,
            custom: (opts: any) => {
              const data = this.data()[opts.dataPointIndex];
              if (!data) return;
              const amount = 'amount' in data ? data.amount : data.grossAmount;
              const description = 'desc' in data ? data.desc : '';
              const title = 'title' in data ? data.title : '';

              return `
        <div class="p-1">
          <strong>${title}</strong>
        </div>
        <div class="p-1">
            ${this.translate.instant('INCIDENT_TYPE.TOOLTIP_AMOUNT')}: ${amount}
        </div>
        <div class="p-1">${this.translate.instant('INCIDENT_TYPE.TOOLTIP_INCIDENT_VOLUME')}: ${description || '-'}</div>
       
    `;
            }
          }


        };
        break;

      case 'line':
        this.chartOptions = {
          series: [
            {
              name: this.hoverLabels,
              data: this.data().map((item: any) => item.amount),
            },
          ],
          chart: {
            height: '300',
            width: '100%',
            type: 'area',
            animations: { enabled: false },
            zoom: {
              enabled: true,
              type: 'x',
              autoScaleYaxis: true,
              allowMouseWheelZoom: false,
            },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: false,
                reset: true
              }
            },
          },
          colors: this.getColors(this.graphColors),
          stroke: {
            curve: 'smooth',
          },
          dataLabels: {
            enabled: false,
          },
          fill: {
            type: 'gradient',
            gradient: {
              opacityFrom: 0.96,
              opacityTo: 0.77,
            },
          },
          markers: {
            size: 1,
          },
          xaxis: {
            categories: this.data().map((item: any) => item.title.split('T')[0]),
            labels: {
              show: this.isXLabels,
            },
            tooltip: {
              enabled: false,
            },
          },
          yaxis: {
            labels: {
              show: this.isYLabels,
            },
          },
        };
        break;

      case 'pie':
        this.chartOptions = {
          series: this.data().map((item: any) => item.amount),
          chart: {
            height: Number(this.height),
            width: '100%',
            type: 'donut',
          },
          labels: this.data().map((item: any) => item.title),
          colors: this.getColors(this.graphColors),
          legend: {
            show: this.isLegend,
            position: 'bottom',
            floating: false,
            fontSize: '14px',
            labels: {
              colors: undefined,
              useSeriesColors: false,
            },
          },
        };
        break;

      case 'treemap':
        const fmt = (val: number) => {
          if (val / 1000 > 1) {
            return val.toLocaleString() + '€';
          } else if (val < 100) {
            return val + 'M';
          } else {
            return val.toLocaleString() + 'M';
          }
        };
        this.chartOptions = {
          series: [
            {
              data: this.data().map((item: any) => {
                return {
                  x: item.category,
                  y: item.grossAmount
                }
              })

            },
          ],
          chart: {
            width: '100%',
            height: 300,
            type: 'treemap',
            events: {
              dataPointSelection: () => this.router.navigate(['/reports/healthcareProviders'])
            }
          },
          plotOptions: {
            treemap: {
              distributed: true,
              enableShades: true,
              shadeIntensity: 0.2,
            },
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
            formatter: (text: string, opts: any) => {
              return `${text} (${fmt(opts.value)})`;
            },
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['transparent'],
          },
          colors: this.colors,
          yaxis: {
            labels: {
              show: true,
            },
          },
          tooltip: {
            y: {
              formatter: (val: number) => fmt(val)
            },
          },
        };
        break;
    }
  }
}
